/**
 * Translation.
 *
 * A dictionary and a hook, not a library. The portal has one plural rule to worry
 * about, no RTL, no lazy-loaded namespaces and about 400 strings — i18next would be
 * more configuration than content.
 *
 * Italian is the source language and the default: the client is in Milan and their
 * office staff work in Italian. English exists so the freelancer building this, and
 * anyone maintaining it later, can read the screens.
 *
 * `it` is the shape everything else is checked against, so a missing English key is a
 * compile error rather than a screen that silently shows a dotted path to a user.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { setDateLocale } from '@/lib/format';

import { it } from './it';
import { en } from './en';
import { ar } from './ar';

export type Locale = 'it' | 'en' | 'ar';

/** Languages written right to left. */
export const isRtl = (locale: Locale): boolean => locale === 'ar';

/**
 * The Italian dictionary is `as const`, so its values are literal types. Widening them
 * to `string` is what lets a translation be a different string — without this, `en.ts`
 * would have to repeat the Italian verbatim to typecheck.
 */
export type Translated<T> = { [K in keyof T]: T[K] extends string ? string : Translated<T[K]> };
export type Dictionary = Translated<typeof it>;
/** Every key path in the dictionary, e.g. `nav.shifts`. */
export type TranslationKey = Paths<Dictionary>;

type Paths<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${Paths<T[K]>}`;
}[keyof T & string];

const DICTIONARIES: Record<Locale, Dictionary> = { it, en, ar };
const STORAGE_KEY = 'hh.locale';

/** Values interpolated into `{name}` placeholders. */
export type TranslationValues = Record<string, string | number>;

function lookup(dict: Dictionary, key: string): string | undefined {
  let node: unknown = dict;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, values?: TranslationValues): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

export interface I18n {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: TranslationKey, values?: TranslationValues) => string;
  /**
   * Italian and English disagree about pluralisation often enough that a shared
   * `{count}` template does not work: "1 turno escluso" / "2 turni esclusi" changes the
   * noun AND the adjective. Callers pass both forms and get the right one.
   */
  plural: (count: number, one: TranslationKey, many: TranslationKey, values?: TranslationValues) => string;
}

const I18nContext = createContext<I18n | null>(null);

function readStored(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'it' || raw === 'en' || raw === 'ar') return raw;
  } catch {
    // Storage blocked. Fall through to the default.
  }
  // Only English speakers get English; everyone else gets the client's own language.
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('en')) return 'en';
  return 'it';
}

/**
 * `lang` and `dir` on <html>. Everything that mirrors (flex rows, logical margins, text
 * alignment, the sidebar side) follows `dir`, so switching language re-lays the page out
 * with no reload. en-GB, not en: `lang` is what a browser uses to order the fields of a
 * native date picker, and bare "en" resolves to US — mm/dd/yyyy.
 */
function applyDocumentLocale(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.lang = locale === 'en' ? 'en-GB' : locale;
  root.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = readStored();
    // Before the first render, not in an effect: an effect runs AFTER the first paint,
    // so an English visitor would see one frame of Italian dates.
    setDateLocale(initial);
    applyDocumentLocale(initial);
    return initial;
  });

  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    // date-fns formatters are plain functions, not hooks, so they read a module-level
    // locale that this sets. Done here rather than in an effect so it is already correct
    // by the time the re-render this triggers formats anything.
    setDateLocale(next);
    // Synchronously, so the direction flips in the same frame as the new strings.
    applyDocumentLocale(next);
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Applies for this session even if it can't be remembered.
    }
  }, []);

  const value = useMemo<I18n>(() => {
    const dict = DICTIONARIES[locale];

    const t = (key: TranslationKey, values?: TranslationValues) => {
      // Fall back to Italian rather than to the key: a half-translated screen is
      // usable, a screen reading "alerts.acknowledge.title" is not.
      const template = lookup(dict, key) ?? lookup(it, key);
      if (template === undefined) {
        if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
        return key;
      }
      return interpolate(template, values);
    };

    return {
      locale,
      setLocale,
      t,
      plural: (count, one, many, values) =>
        t(count === 1 ? one : many, { count, ...values }),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/**
 * Reading direction for the few things drawn by hand. A "forward" arrow between two
 * values (from → to) points the way the text reads, so it is flipped in Arabic; the
 * layout itself (flex rows, logical margins, the sidebar side) follows `dir` on its own.
 */
export function useDir(): { rtl: boolean; forward: string; back: string } {
  const rtl = isRtl(useI18n().locale);
  return { rtl, forward: rtl ? '←' : '→', back: rtl ? '→' : '←' };
}

/** Shorthand for the common case. */
export function useT() {
  return useI18n().t;
}
